import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, History, Pencil, Phone, PhoneOff, ShieldCheck, WifiOff,
} from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "./ui/drawer";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Skeleton } from "./ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { api, errMsg, fmtDateTime, istDateISO } from "../lib/api";
import { QUEUE_EVENT, isNetworkError, queueAdd } from "../lib/offline";
import { cn } from "../lib/utils";

/**
 * Phase 134 — owner mobile number verification.
 *
 * Tax collectors and supervisors must verify the owner's mobile number before they can record a visit,
 * mark a property visited or collect a payment — once per property, per staff member, per IST day. They
 * either CONFIRM the number on record ("Number is correct, no change needed") or UPDATE it. Properties with
 * no number on record can only be updated. Works offline: the step is queued (kind "mobile_verify") and a
 * per-day marker on the device keeps the gate open until the queue syncs.
 */
export const MOBILE_RE = /^[6-9]\d{9}$/;

export const normalizeMobile = (raw) => {
  let d = String(raw || "").replace(/\D/g, "");
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
  else if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  return d;
};
export const isValidMobile = (raw) => MOBILE_RE.test(normalizeMobile(raw));
/** 9876543210 → "98765 43210" (Indian grouping) */
export const fmtMobile = (raw) => {
  const d = normalizeMobile(raw);
  return d.length === 10 ? `${d.slice(0, 5)} ${d.slice(5)}` : (raw || "");
};

export const VERIFY_REQUIRED_MESSAGE = "Verify the owner's mobile number before recording a visit or collection.";

// ---- device-side per-day marker (offline + instant UI) ----------------------------------------------
const markerKey = (code, propId, userId) => `mv:${code}:${propId}:${userId || "anon"}:${istDateISO()}`;
export const readLocalVerification = (code, propId, userId) => {
  try { return JSON.parse(localStorage.getItem(markerKey(code, propId, userId)) || "null"); } catch { return null; }
};
export const writeLocalVerification = (code, propId, userId, data) => {
  try { localStorage.setItem(markerKey(code, propId, userId), JSON.stringify({ ...data, at: new Date().toISOString() })); } catch { /* ignore */ }
};

/**
 * useMobileVerification — one hook per property page.
 *   const mv = useMobileVerification({ code, property, verification: detail.mobile_verification, userId, online, onChanged });
 *   mv.gate(() => setVisitOpen(true))   // runs immediately when verified, else opens the drawer and runs after
 *   <MobileVerifyCard mv={mv} />  <MobileVerifyDrawer mv={mv} />
 */
export function useMobileVerification({ code, property, verification, userId, online = true, onChanged }) {
  const propId = property?.id;
  const [local, setLocal] = useState(() => (propId ? readLocalVerification(code, propId, userId) : null));
  const [session, setSession] = useState(null); // {mobile, action, offline} after a verification in this page session
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState(null);   // "confirm" | "update" — which option the drawer opens on
  const [busy, setBusy] = useState(false);
  const pending = useRef(null);

  useEffect(() => { setLocal(propId ? readLocalVerification(code, propId, userId) : null); setSession(null); }, [code, propId, userId]);

  const required = verification ? verification.required !== false : true;
  const verified = !required || !!(session || verification?.verified_today || local);
  const mobile = normalizeMobile(session?.mobile ?? local?.mobile ?? verification?.mobile ?? property?.mobile) || null;
  const hasMobile = !!mobile;
  // "saved on device, will sync" — only while the server has not yet seen today's verification
  const pendingSync = !!(session?.offline || local?.offline) && !verification?.verified_today;

  // Once the offline queue replays (device back online), pull the server state so the card drops the
  // "will sync when online" note and shows the synced number/time.
  const onChangedRef = useRef(onChanged);
  useEffect(() => { onChangedRef.current = onChanged; }, [onChanged]);
  useEffect(() => {
    if (!pendingSync || !online) return undefined;
    let timer = null;
    const refresh = () => { clearTimeout(timer); timer = setTimeout(() => onChangedRef.current?.({ synced: true }), 800); };
    window.addEventListener(QUEUE_EVENT, refresh);
    return () => { clearTimeout(timer); window.removeEventListener(QUEUE_EVENT, refresh); };
  }, [pendingSync, online]);
  useEffect(() => {
    // server confirmed today's verification — clear the device marker's offline flag so it stays cleared on reload
    if (verification?.verified_today && (local?.offline || session?.offline) && propId) {
      if (local?.offline) { writeLocalVerification(code, propId, userId, { ...local, offline: false }); setLocal(readLocalVerification(code, propId, userId)); }
      if (session?.offline) setSession((s) => (s ? { ...s, offline: false } : s));
    }
  }, [verification?.verified_today, local?.offline, session?.offline, code, propId, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const request = useCallback((mode = null, after = null) => {
    pending.current = after;
    setPreset(mode);
    setOpen(true);
  }, []);

  /** Run `fn` now if verified, otherwise open the verification step and run it once completed. */
  const gate = useCallback((fn) => {
    if (verified) { fn?.(); return true; }
    toast.info(VERIFY_REQUIRED_MESSAGE);
    request(null, fn);
    return false;
  }, [verified, request]);

  const submit = useCallback(async ({ action, mobile: newMobile }) => {
    if (!propId) return false;
    const number = action === "update" ? normalizeMobile(newMobile) : mobile;
    if (action === "update" && !MOBILE_RE.test(number)) {
      toast.error("Enter a valid 10-digit mobile number starting with 6, 7, 8 or 9");
      return false;
    }
    if (action === "confirm" && !mobile) {
      toast.error("There is no number on record — enter the owner's mobile number");
      return false;
    }
    const finish = (finalMobile, offline) => {
      writeLocalVerification(code, propId, userId, { mobile: finalMobile, action, offline });
      setLocal(readLocalVerification(code, propId, userId));
      setSession({ mobile: finalMobile, action, offline });
      setOpen(false);
      const after = pending.current; pending.current = null;
      if (after) setTimeout(after, 50);
    };
    const offlineSave = async () => {
      await queueAdd(code, "mobile_verify", { property_id: propId, action, mobile: action === "update" ? number : undefined,
        verified_at: new Date().toISOString() }, { label: `Mobile ${action === "update" ? "updated" : "confirmed"} • ${property?.pin || ""}` });
      toast.success(action === "update" ? "New number saved on device — syncs when back online" : "Number confirmed on device — syncs when back online");
      finish(number, true);
      onChanged?.({ mobile: number, offline: true });
    };
    setBusy(true);
    try {
      if (!online) { await offlineSave(); return true; }
      try {
        const r = await api.post(`/t/${code}/properties/${propId}/mobile/verify`,
          { action, mobile: action === "update" ? number : undefined, verified_at: new Date().toISOString() });
        toast.success(r.data.changed ? `Mobile number updated to ${fmtMobile(r.data.mobile)}` : "Mobile number confirmed — no change needed");
        finish(normalizeMobile(r.data.mobile), false);
        onChanged?.(r.data);
        return true;
      } catch (e) {
        if (isNetworkError(e)) { await offlineSave(); return true; }
        toast.error(errMsg(e));
        return false;
      }
    } finally { setBusy(false); }
  }, [code, propId, userId, mobile, online, onChanged, property?.pin]);

  const info = useMemo(() => ({
    lastVerifiedAt: session ? new Date().toISOString() : (local?.at || verification?.last_verified_at || null),
    lastVerifiedBy: session || local ? "you" : (verification?.last_verified_by_name || null),
    lastVerifiedByCode: session || local ? null : (verification?.last_verified_by_employee_id || null),
    lastAction: session?.action || local?.action || verification?.last_action || null,
    offline: pendingSync,
    numberCount: verification?.number_count || 0,
  }), [session, local, verification, pendingSync]);

  return { code, propId, property, required, verified, mobile, hasMobile, open, setOpen, preset, request, gate, submit, busy, info, online };
}

// ---- the verification step ------------------------------------------------------------------------------
export function MobileVerifyDrawer({ mv, testId = "mobile-verify" }) {
  const { open, setOpen, preset, mobile, hasMobile, submit, busy, online } = mv;
  const [choice, setChoice] = useState("confirm");
  const [value, setValue] = useState("");
  useEffect(() => {
    if (open) { setChoice(!hasMobile ? "update" : (preset || "confirm")); setValue(""); }
  }, [open, preset, hasMobile]);
  const valid = isValidMobile(value);
  const sameAsCurrent = valid && normalizeMobile(value) === mobile;
  const canSubmit = !busy && (choice === "confirm" ? hasMobile : valid);

  return (
    <Drawer open={open} onOpenChange={(v) => { if (!busy) setOpen(v); }}>
      <DrawerContent className="max-h-[92vh]" data-testid={`${testId}-drawer`}>
        <DrawerHeader className="text-left">
          <DrawerTitle className="font-heading flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" aria-hidden="true" /> Verify owner's mobile number
          </DrawerTitle>
          <DrawerDescription>
            Required once per property per day before you record a visit or collect a payment. Your name and the time are saved with it.
          </DrawerDescription>
        </DrawerHeader>
        <div className="px-4 pb-6 space-y-4 overflow-y-auto max-w-lg mx-auto w-full">
          <div className={cn("rounded-xl border px-4 py-3 flex items-center gap-3",
            hasMobile ? "bg-muted/60" : "bg-[hsl(var(--warning))]/10 border-[hsl(var(--warning))]/30")} data-testid={`${testId}-current`}>
            {hasMobile ? <Phone className="h-5 w-5 text-primary shrink-0" /> : <PhoneOff className="h-5 w-5 text-[hsl(var(--warning))] shrink-0" />}
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Number on record</p>
              {hasMobile
                ? <p className="text-xl font-semibold tabular-nums tracking-wide" data-testid={`${testId}-current-number`}>{fmtMobile(mobile)}</p>
                : <p className="text-sm font-medium text-[hsl(var(--warning))]" data-testid={`${testId}-no-number`}>No mobile number on record — enter the owner's number to continue</p>}
            </div>
          </div>

          <RadioGroup value={choice} onValueChange={setChoice} className="gap-2" data-testid={`${testId}-choice`}>
            <label className={cn("flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors",
              choice === "confirm" && "border-primary bg-accent/40", !hasMobile && "opacity-50 cursor-not-allowed")}>
              <RadioGroupItem value="confirm" id={`${testId}-opt-confirm`} disabled={!hasMobile} className="mt-0.5" data-testid={`${testId}-option-confirm`} />
              <div>
                <p className="text-sm font-medium">Number is correct, no change needed</p>
                <p className="text-xs text-muted-foreground">{hasMobile ? "You spoke to the owner and the number above is right." : "Not available — there is no number on record yet."}</p>
              </div>
            </label>
            <label className={cn("flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors", choice === "update" && "border-primary bg-accent/40")}>
              <RadioGroupItem value="update" id={`${testId}-opt-update`} className="mt-0.5" data-testid={`${testId}-option-update`} />
              <div className="flex-1">
                <p className="text-sm font-medium">{hasMobile ? "Update the mobile number" : "Enter the owner's mobile number"}</p>
                <p className="text-xs text-muted-foreground">{hasMobile ? "The old number stays in the history; only the new one is shown." : "It becomes the number on record for this property."}</p>
                {choice === "update" && (
                  <div className="mt-2 space-y-1">
                    <Label htmlFor={`${testId}-input`} className="text-xs">New mobile number *</Label>
                    <Input id={`${testId}-input`} type="tel" inputMode="numeric" autoFocus maxLength={14} className="h-11 text-base tabular-nums"
                      placeholder="10-digit mobile number" value={value}
                      onChange={(e) => setValue(e.target.value.replace(/[^\d+\s-]/g, ""))}
                      onKeyDown={(e) => { if (e.key === "Enter" && canSubmit) submit({ action: choice, mobile: value }); }}
                      aria-invalid={value && !valid ? "true" : undefined} data-testid={`${testId}-input`} />
                    {value && !valid && <p className="text-xs text-destructive" data-testid={`${testId}-input-error`}>Enter a valid 10-digit Indian mobile number (starts with 6–9).</p>}
                    {sameAsCurrent && <p className="text-xs text-muted-foreground" data-testid={`${testId}-same-hint`}>Same as the number on record — this will be saved as a confirmation.</p>}
                  </div>
                )}
              </div>
            </label>
          </RadioGroup>

          {!online && (
            <p className="text-xs text-amber-900 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 flex items-center gap-2" data-testid={`${testId}-offline-note`}>
              <WifiOff className="h-3.5 w-3.5 shrink-0" /> You are offline — the verification is saved on this device and syncs automatically.
            </p>
          )}

          <Button size="lg" className="w-full h-12" disabled={!canSubmit} onClick={() => submit({ action: choice, mobile: value })} data-testid={`${testId}-submit`}>
            {busy ? "Saving…" : choice === "confirm" ? "Confirm number & continue" : "Save new number & continue"}
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

// ---- status card on the property page -------------------------------------------------------------------
export function MobileVerifyCard({ mv, testId = "mobile-verify-card", showHistory = true, className = "" }) {
  const { code, propId, required, verified, mobile, hasMobile, request, info } = mv;
  const [histOpen, setHistOpen] = useState(false);
  if (!required) return null;
  return (
    <Card className={cn("shadow-sm", !verified && "border-[hsl(var(--warning))]/60", className)} data-testid={testId} data-verified={verified ? "true" : "false"}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div className={cn("h-10 w-10 rounded-full flex items-center justify-center shrink-0",
              verified ? "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" : "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]")}>
              {hasMobile ? <Phone className="h-5 w-5" aria-hidden="true" /> : <PhoneOff className="h-5 w-5" aria-hidden="true" />}
            </div>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Owner's mobile number</p>
              {hasMobile ? (
                <a href={`tel:${mobile}`} className="text-lg font-semibold tabular-nums tracking-wide text-primary hover:underline" data-testid={`${testId}-number`}>{fmtMobile(mobile)}</a>
              ) : (
                <p className="text-sm font-medium text-[hsl(var(--warning))]" data-testid={`${testId}-no-number`}>No mobile number on record</p>
              )}
              <p className="text-[11px] text-muted-foreground mt-0.5" data-testid={`${testId}-meta`}>
                {verified
                  ? <>Verified today{info.lastVerifiedBy === "you" ? " by you" : ""}{info.lastAction === "updated" ? " (number updated)" : ""}{info.offline ? " · will sync when online" : ""}</>
                  : info.lastVerifiedAt
                    ? <>Last verified {fmtDateTime(info.lastVerifiedAt)}{info.lastVerifiedBy ? ` by ${info.lastVerifiedBy}${info.lastVerifiedByCode ? ` (${info.lastVerifiedByCode})` : ""}` : ""}</>
                    : "Never verified"}
              </p>
            </div>
          </div>
          {verified ? (
            <Badge variant="outline" className="gap-1 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/20 shrink-0" data-testid={`${testId}-status`}>
              <CheckCircle2 className="h-3 w-3" /> Verified today
            </Badge>
          ) : (
            <Badge variant="outline" className="gap-1 bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30 shrink-0" data-testid={`${testId}-status`}>
              <AlertTriangle className="h-3 w-3" /> Verification required
            </Badge>
          )}
        </div>

        {!verified ? (
          <>
            <p className="text-xs text-muted-foreground">
              Ask the owner for their mobile number before you record a visit or collect payment today.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button variant="outline" className="h-11 justify-center" disabled={!hasMobile} onClick={() => request("confirm")} data-testid={`${testId}-confirm-button`}>
                <CheckCircle2 className="h-4 w-4 mr-1.5" /> Number is correct
              </Button>
              <Button className="h-11 justify-center" onClick={() => request("update")} data-testid={`${testId}-update-button`}>
                <Pencil className="h-4 w-4 mr-1.5" /> {hasMobile ? "Update number" : "Enter mobile number"}
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="sm" className="h-9 text-muted-foreground" onClick={() => request("update")} data-testid={`${testId}-change-button`}>
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> Change number
            </Button>
            {showHistory && (
              <Button variant="ghost" size="sm" className="h-9 text-muted-foreground" onClick={() => setHistOpen((v) => !v)} data-testid={`${testId}-history-toggle`}>
                <History className="h-3.5 w-3.5 mr-1.5" /> Number history {histOpen ? <ChevronUp className="h-3.5 w-3.5 ml-1" /> : <ChevronDown className="h-3.5 w-3.5 ml-1" />}
              </Button>
            )}
          </div>
        )}
        {showHistory && histOpen && verified && (
          <MobileNumberHistory code={code} propId={propId} compact testId={`${testId}-history`} refreshKey={info.lastVerifiedAt} />
        )}
      </CardContent>
    </Card>
  );
}

// ---- read-only history (admin sheet, collector / supervisor pages) --------------------------------------
export const ACTION_LABELS = { initial: "On record", updated: "Updated", confirmed: "Confirmed correct" };

export function MobileNumberHistory({ code, propId, compact = false, testId = "mobile-number-history", refreshKey = null, title = "Number History" }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const load = useCallback(() => {
    if (!propId) return;
    setError(null);
    api.get(`/t/${code}/properties/${propId}/mobile/history`).then((r) => setData(r.data)).catch((e) => setError(errMsg(e)));
  }, [code, propId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (error) return <p className="text-xs text-destructive" data-testid={`${testId}-error`}>{error}</p>;
  if (!data) return <div className="space-y-2" data-testid={`${testId}-loading`}><Skeleton className="h-4 w-40" /><Skeleton className="h-10 w-full" /></div>;
  const old = data.numbers.filter((n) => !n.is_current);
  const current = data.numbers.find((n) => n.is_current);
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <p className={cn("font-medium flex items-center gap-1.5", compact ? "text-xs" : "text-sm")}>
          <History className="h-3.5 w-3.5" /> {title}
          <Badge variant="secondary" className="text-[10px]" data-testid={`${testId}-count`}>{old.length} old</Badge>
        </p>
        {data.counts?.confirmations > 0 && (
          <button type="button" className="text-[11px] text-muted-foreground underline underline-offset-4 hover:text-foreground" onClick={() => setShowLog((v) => !v)} data-testid={`${testId}-log-toggle`}>
            Verification log ({data.counts.confirmations})
          </button>
        )}
      </div>
      {current && (
        <p className="text-xs text-muted-foreground" data-testid={`${testId}-current`}>
          Current: <span className="font-semibold tabular-nums text-foreground">{fmtMobile(current.mobile)}</span>
          {current.action === "updated" && <> · set by {current.by_label} on {fmtDateTime(current.at)}</>}
        </p>
      )}
      {old.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid={`${testId}-empty`}>No old numbers — the number on record has never been changed.</p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table data-testid={`${testId}-table`}>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Old number</TableHead>
                <TableHead className="text-xs">Entered by</TableHead>
                <TableHead className="text-xs">Replaced on</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {old.map((n, i) => {
                const replacedBy = data.numbers.find((x) => x.previous_mobile === n.mobile && x.at > (n.at || ""));
                return (
                  <TableRow key={n.id || `${n.mobile}-${i}`} data-testid={`${testId}-row-${n.mobile}`}>
                    <TableCell className="text-sm tabular-nums font-medium">{fmtMobile(n.mobile)}</TableCell>
                    <TableCell className="text-xs">{n.by_label}<p className="text-[11px] text-muted-foreground">{ACTION_LABELS[n.action] || n.action}{n.at ? ` · ${fmtDateTime(n.at)}` : ""}</p></TableCell>
                    <TableCell className="text-xs">{replacedBy ? <>{fmtDateTime(replacedBy.at)}<p className="text-[11px] text-muted-foreground">by {replacedBy.by_label}</p></> : "—"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {showLog && (
        <ul className="space-y-1 border-l-2 border-border pl-3" data-testid={`${testId}-log`}>
          {data.confirmations.map((c) => (
            <li key={c.id} className="text-[11px] text-muted-foreground" data-testid={`${testId}-log-row`}>
              <CheckCircle2 className="h-3 w-3 inline mr-1 text-[hsl(var(--success))]" />
              <span className="tabular-nums text-foreground">{fmtMobile(c.mobile)}</span> confirmed correct by {c.by_label} · {fmtDateTime(c.at)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
